// Responsive viewport plumbing shared by game clients. Centralizes every signal
// that should trigger a relayout (window resize, orientationchange,
// ResizeObserver on the canvas host, visualViewport resize/scroll) plus
// document-visibility changes, and coalesces them into a single rAF-debounced
// callback. Every external dependency is injectable, and start()/stop() are
// idempotent so repeated calls never leak or duplicate listeners.
import { NO_INSETS } from './layout.js';
let insetProbe = null;
/** Reads the live viewport size, dpr and coarse-pointer capability. */
export function readViewport(root, win = typeof window !== 'undefined'
    ? window
    : { innerWidth: 1, innerHeight: 1, devicePixelRatio: 1 }) {
    const width = root?.clientWidth || win.innerWidth || 1;
    const height = root?.clientHeight || win.innerHeight || 1;
    const dpr = win.devicePixelRatio || 1;
    let coarsePointer = false;
    try {
        coarsePointer = typeof win.matchMedia === 'function' && win.matchMedia('(pointer: coarse)').matches;
    }
    catch {
        coarsePointer = false;
    }
    return { width, height, dpr, coarsePointer };
}
/**
 * Reads the CSS safe-area insets (env(safe-area-inset-*)) via a hidden probe
 * element. Returns zeros where the environment does not support safe areas.
 */
export function readSafeAreaInsets(doc = typeof document !== 'undefined' ? document : undefined) {
    if (!doc || typeof doc.createElement !== 'function' || typeof getComputedStyle !== 'function' || !doc.body) {
        return { ...NO_INSETS };
    }
    if (!insetProbe || !doc.body.contains(insetProbe)) {
        insetProbe = doc.createElement('div');
        insetProbe.setAttribute('aria-hidden', 'true');
        insetProbe.style.cssText = [
            'position:fixed', 'top:0', 'left:0', 'width:0', 'height:0',
            'pointer-events:none', 'visibility:hidden',
            'padding-top:env(safe-area-inset-top)',
            'padding-right:env(safe-area-inset-right)',
            'padding-bottom:env(safe-area-inset-bottom)',
            'padding-left:env(safe-area-inset-left)',
        ].join(';');
        doc.body.appendChild(insetProbe);
    }
    const cs = getComputedStyle(insetProbe);
    const px = (v) => {
        const n = Number.parseFloat(v);
        return Number.isFinite(n) && n > 0 ? n : 0;
    };
    return {
        top: px(cs.paddingTop),
        right: px(cs.paddingRight),
        bottom: px(cs.paddingBottom),
        left: px(cs.paddingLeft),
    };
}
function clamp(v, lo, hi) {
    return Math.max(lo, Math.min(hi, Number.isFinite(v) ? v : lo));
}
/**
 * Combined inset mapping the FULL layout viewport down to the currently visible
 * browser area. Folds together CSS safe-area insets and the regions hidden by
 * browser chrome derived from window.visualViewport offset/size. Falls back to
 * safe-area-only when visualViewport is unavailable. All chrome components are
 * clamped so a transient reading never throws the layout off-screen.
 */
export function readMobileInsets(win = typeof window !== 'undefined' ? window : {}, root, doc = typeof document !== 'undefined' ? document : undefined) {
    const safe = readSafeAreaInsets(doc ?? undefined);
    const vv = win.visualViewport;
    if (!vv)
        return safe;
    const layoutW = root?.clientWidth || win.innerWidth || vv.width || 1;
    const layoutH = root?.clientHeight || win.innerHeight || vv.height || 1;
    const offTop = clamp(vv.offsetTop || 0, 0, 240);
    const offLeft = clamp(vv.offsetLeft || 0, 0, 120);
    const chromeBottom = clamp(layoutH - vv.height - (vv.offsetTop || 0), 0, 200);
    const chromeRight = clamp(layoutW - vv.width - (vv.offsetLeft || 0), 0, 120);
    return {
        top: offTop + safe.top,
        left: offLeft + safe.left,
        bottom: chromeBottom + safe.bottom,
        right: chromeRight + safe.right,
    };
}
export class ViewportManager {
    cfg;
    win;
    doc;
    readVp;
    readInsets;
    running = false;
    scheduled = false;
    rafHandle;
    ro = null;
    bound = [];
    constructor(cfg) {
        this.cfg = cfg;
        this.win =
            cfg.win ??
                (typeof window !== 'undefined'
                    ? window
                    : { addEventListener() { }, removeEventListener() { } });
        this.doc = cfg.doc ?? (typeof document !== 'undefined' ? document : undefined);
        this.readVp = cfg.readViewport ?? (() => readViewport(cfg.gameRoot, this.win));
        this.readInsets = cfg.readInsets ?? (() => readMobileInsets(this.win, cfg.gameRoot));
    }
    /** Starts listening. Idempotent: a second call without stop() is a no-op. */
    start() {
        if (this.running)
            return;
        this.running = true;
        const relayout = () => this.schedule();
        this.listen(this.win, 'resize', relayout);
        this.listen(this.win, 'orientationchange', relayout);
        const vv = this.win.visualViewport;
        if (vv) {
            this.listen(vv, 'resize', relayout);
            this.listen(vv, 'scroll', relayout);
        }
        if (this.doc) {
            const onVisibility = () => this.cfg.onVisibility?.(Boolean(this.doc?.hidden));
            this.listen(this.doc, 'visibilitychange', onVisibility);
        }
        const factory = this.cfg.makeResizeObserver ?? defaultResizeObserverFactory;
        this.ro = factory(relayout);
        if (this.ro && this.cfg.gameRoot)
            this.ro.observe(this.cfg.gameRoot);
        // Emit an initial layout synchronously so the first frame is correct.
        this.emit();
    }
    /** Stops listening and releases every listener/observer. Idempotent. */
    stop() {
        if (!this.running)
            return;
        this.running = false;
        for (const { target, type, fn } of this.bound)
            target.removeEventListener(type, fn);
        this.bound.length = 0;
        this.ro?.disconnect();
        this.ro = null;
        if (this.rafHandle !== undefined && this.win.cancelAnimationFrame) {
            this.win.cancelAnimationFrame(this.rafHandle);
        }
        this.rafHandle = undefined;
        this.scheduled = false;
    }
    /** Forces an immediate relayout emit (e.g. after asset load). */
    emit() {
        this.cfg.onLayout(this.readVp(), this.readInsets());
    }
    listen(target, type, fn) {
        target.addEventListener(type, fn);
        this.bound.push({ target, type, fn });
    }
    /** Coalesces a burst of events into a single relayout on the next frame. */
    schedule() {
        if (this.scheduled)
            return;
        this.scheduled = true;
        const run = () => {
            this.scheduled = false;
            if (this.running)
                this.emit();
        };
        if (typeof this.win.requestAnimationFrame === 'function') {
            this.rafHandle = this.win.requestAnimationFrame(run);
        }
        else {
            run();
        }
    }
}
function defaultResizeObserverFactory(cb) {
    if (typeof ResizeObserver === 'undefined')
        return null;
    return new ResizeObserver(() => cb());
}
//# sourceMappingURL=responsive.js.map